import { useState } from 'react'
import { useBoosts, useCreateBoost, useUpdateBoost, useToggleBoost } from '@/hooks/useBoosts'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { StatusBadge } from '@/components/common/StatusBadge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Boost, BoostCreateRequest, BoostUpdateRequest } from '@/types'
import { formatCurrency, getApiErrorMessage } from '@/lib/utils'
import { toast } from 'sonner'
import { Plus, Pencil, Rocket, Zap, Target } from 'lucide-react'

const emptyCreateForm: BoostCreateRequest = {
  boost_id: '',
  name: '',
  description: '',
  credits: 0,
  price_usd: 0,
  ideal_for: '',
  sort_order: 0,
}

export const BoostsListPage = () => {
  const { data: boosts, isLoading } = useBoosts()
  const createBoost = useCreateBoost()
  const updateBoost = useUpdateBoost()
  const toggleBoost = useToggleBoost()

  const [createOpen, setCreateOpen] = useState(false)
  const [createForm, setCreateForm] = useState<BoostCreateRequest>(emptyCreateForm)
  const [editBoost, setEditBoost] = useState<Boost | null>(null)
  const [editForm, setEditForm] = useState<BoostUpdateRequest>({})

  const openCreate = () => {
    setCreateForm(emptyCreateForm)
    setCreateOpen(true)
  }

  const openEdit = (boost: Boost) => {
    setEditForm({
      name: boost.name,
      description: boost.description,
      credits: boost.credits,
      price_usd: boost.price_usd,
      ideal_for: boost.ideal_for || '',
      sort_order: boost.sort_order,
    })
    setEditBoost(boost)
  }

  const handleCreate = async () => {
    try {
      await createBoost.mutateAsync(createForm)
      setCreateOpen(false)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al crear boost'))
    }
  }

  const handleSave = async () => {
    if (!editBoost) return
    try {
      await updateBoost.mutateAsync({ id: editBoost.id, updates: editForm })
      setEditBoost(null)
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Error al actualizar boost'))
    }
  }

  const calcPricePer1k = (credits: number, price: number) => {
    if (credits <= 0) return 0
    return (price / credits) * 1000
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold">Boosts</h1>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-72" />
          ))}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Boosts</h1>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Crear boost
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {boosts?.map((boost) => (
          <Card key={boost.id} className={!boost.is_active ? 'opacity-60' : ''}>
            <CardHeader className="flex flex-row items-start justify-between pb-2">
              <div>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Rocket className="h-4 w-4 text-primary" />
                  {boost.name}
                </CardTitle>
                <p className="mt-1 font-mono text-xs text-muted-foreground">{boost.boost_id}</p>
              </div>
              <StatusBadge status={boost.is_active ? 'active' : 'inactive'} />
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="line-clamp-2 text-sm text-muted-foreground">
                {boost.description || 'Sin descripcion'}
              </p>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-muted-foreground">Creditos</p>
                  <p className="flex items-center gap-1 text-lg font-bold">
                    <Zap className="h-4 w-4 text-yellow-500" />
                    {boost.credits.toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">Precio</p>
                  <p className="text-lg font-bold">{formatCurrency(boost.price_usd)}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <p className="text-muted-foreground">Precio/1k</p>
                  <p className="font-medium">{formatCurrency(boost.price_per_1k)}</p>
                </div>
                {boost.discount_label && (
                  <div>
                    <p className="text-muted-foreground">Descuento</p>
                    <p className="font-medium text-green-600">{boost.discount_label}</p>
                  </div>
                )}
              </div>

              {boost.ideal_for && (
                <div className="flex items-center gap-1 text-sm">
                  <Target className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-muted-foreground">Ideal para:</span>
                  <span className="font-medium">{boost.ideal_for}</span>
                </div>
              )}

              <div className="flex items-center justify-between border-t pt-3">
                <span className="text-xs text-muted-foreground">Orden: {boost.sort_order}</span>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={boost.is_active}
                    onCheckedChange={() => toggleBoost.mutate(boost.id)}
                  />
                  <Button variant="ghost" size="sm" onClick={() => openEdit(boost)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {boosts && boosts.length === 0 && (
        <div className="py-12 text-center text-muted-foreground">No hay boosts configurados</div>
      )}

      {/* Create Boost Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear boost</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>ID (slug)</Label>
              <Input
                placeholder="boost_500"
                value={createForm.boost_id}
                onChange={(e) => setCreateForm((f) => ({ ...f, boost_id: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Nombre</Label>
              <Input
                placeholder="Boost 500"
                value={createForm.name}
                onChange={(e) => setCreateForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Descripcion</Label>
              <Input
                placeholder="Descripcion del boost..."
                value={createForm.description}
                onChange={(e) => setCreateForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Creditos</Label>
                <Input
                  type="number"
                  min={0}
                  value={createForm.credits}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, credits: Number(e.target.value) }))
                  }
                />
              </div>
              <div className="grid gap-2">
                <Label>Precio (USD)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={createForm.price_usd}
                  onChange={(e) =>
                    setCreateForm((f) => ({ ...f, price_usd: Number(e.target.value) }))
                  }
                />
              </div>
            </div>
            {createForm.credits > 0 && createForm.price_usd > 0 && (
              <p className="text-sm text-muted-foreground">
                Precio/1k:{' '}
                {formatCurrency(calcPricePer1k(createForm.credits, createForm.price_usd))}
              </p>
            )}
            <div className="grid gap-2">
              <Label>Ideal para</Label>
              <Input
                placeholder="Ej: Campanas puntuales"
                value={createForm.ideal_for}
                onChange={(e) => setCreateForm((f) => ({ ...f, ideal_for: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Orden</Label>
              <Input
                type="number"
                min={0}
                value={createForm.sort_order}
                onChange={(e) =>
                  setCreateForm((f) => ({ ...f, sort_order: Number(e.target.value) }))
                }
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreate}
              disabled={createBoost.isPending || !createForm.boost_id || !createForm.name}
            >
              {createBoost.isPending ? 'Creando...' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Boost Dialog */}
      <Dialog open={!!editBoost} onOpenChange={(open) => !open && setEditBoost(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar boost: {editBoost?.name}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label>Nombre</Label>
              <Input
                value={editForm.name ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Descripcion</Label>
              <Input
                value={editForm.description ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Creditos</Label>
                <Input
                  type="number"
                  min={0}
                  value={editForm.credits ?? 0}
                  onChange={(e) => setEditForm((f) => ({ ...f, credits: Number(e.target.value) }))}
                />
              </div>
              <div className="grid gap-2">
                <Label>Precio (USD)</Label>
                <Input
                  type="number"
                  min={0}
                  step={0.01}
                  value={editForm.price_usd ?? 0}
                  onChange={(e) =>
                    setEditForm((f) => ({ ...f, price_usd: Number(e.target.value) }))
                  }
                />
              </div>
            </div>
            {(editForm.credits ?? 0) > 0 && (editForm.price_usd ?? 0) > 0 && (
              <p className="text-sm text-muted-foreground">
                Precio/1k:{' '}
                {formatCurrency(calcPricePer1k(editForm.credits ?? 0, editForm.price_usd ?? 0))}
              </p>
            )}
            <div className="grid gap-2">
              <Label>Ideal para</Label>
              <Input
                value={editForm.ideal_for ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, ideal_for: e.target.value }))}
              />
            </div>
            <div className="grid gap-2">
              <Label>Orden</Label>
              <Input
                type="number"
                min={0}
                value={editForm.sort_order ?? 0}
                onChange={(e) => setEditForm((f) => ({ ...f, sort_order: Number(e.target.value) }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditBoost(null)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={updateBoost.isPending}>
              {updateBoost.isPending ? 'Guardando...' : 'Guardar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
